import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  AdditionalColumnsListDto,
  AllowedDomainListDto,
  OrganizationDto,
  YourTeamDto,
} from '@sib/api';
import { BillingContactProvider, QueryKey } from '@sib/api/enums';
import { useUserSetupStore } from '@sib/auth';
import { SpendBrainTableName } from './preferences';

export const useGetOrganization = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationDto>
): UseQueryResult<OrganizationDto> => {
  const { setOrganization, setActionPermissions } = useUserSetupStore();

  return useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Organization, organizationId],
    queryFn: async () => {
      const data =
        await api.organization.organizationControllerGetOrganizationById(
          organizationId || ''
        );
      setOrganization({
        name: data.name,
      });
      setActionPermissions(data.permissions || {});

      return data;
    },
    ...options,
  });
};

interface UseGetYourTeamPayload {
  organizationId?: string;
  isVectorOrganization: boolean | null;
}

export const useGetYourTeam = (
  { organizationId, isVectorOrganization }: UseGetYourTeamPayload,
  options?: UseQueryOptions<YourTeamDto>
): UseQueryResult<YourTeamDto> =>
  useQuery({
    enabled: !!organizationId && typeof isVectorOrganization === 'boolean',
    queryKey: [QueryKey.Resource.OrganizationYourTeam, organizationId],
    queryFn: async () => {
      const data = await api.organization.organizationControllerGetYourTeam(
        organizationId || '',
        {
          billingContactProvider: isVectorOrganization
            ? BillingContactProvider.Vector
            : BillingContactProvider.Sib,
        }
      );

      return data;
    },
    ...options,
  });

export const useGetAllowedDomains = (
  organizationId?: string,
  options?: UseQueryOptions<AllowedDomainListDto>
): UseQueryResult<AllowedDomainListDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationAllowedDomains, organizationId],
    queryFn: async () =>
      api.organization.organizationControllerFindAllowedDomains(
        organizationId || ''
      ),
    ...options,
  });

interface UseGetAdditionalColumnsPayload {
  organizationId?: string;
  tableName: SpendBrainTableName;
  options?: UseQueryOptions<AdditionalColumnsListDto>;
}

export const useGetAdditionalColumns = ({
  organizationId,
  tableName,
  options,
}: UseGetAdditionalColumnsPayload): UseQueryResult<AdditionalColumnsListDto> =>
  useQuery({
    enabled: !!organizationId && !!tableName,
    queryKey: [
      QueryKey.Resource.OrganizationAdditionalColumns,
      organizationId,
      tableName,
    ],
    queryFn: async () =>
      api.organization.organizationControllerGetAdditionalColumns(
        organizationId || '',
        tableName
      ),
    ...options,
  });
